import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Settings,
  LogOut,
  ExternalLink,
  FileBarChart2,
  UsersRound,
  UserPlus,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  History,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_c5288981-1cfe-4202-bbfa-1fa74e8ddd2d/artifacts/nf5at2sb_6eb7c94a-c17d-437b-a6c9-9665777e6cb6.png";

const links = [
  { to: "/admin", icon: LayoutDashboard, label: "Visão geral", end: true, testid: "nav-overview" },
  { to: "/admin/itens", icon: Package, label: "Itens", testid: "nav-items" },
  { to: "/admin/pedidos", icon: ShoppingBag, label: "Pedidos", testid: "nav-orders" },
  { to: "/admin/inscricoes", icon: UserPlus, label: "Inscrições", testid: "nav-subscriptions" },
  { to: "/admin/retiradas", icon: ClipboardCheck, label: "Retiradas", testid: "nav-pickups" },
  { to: "/admin/relatorios", icon: FileBarChart2, label: "Relatórios", testid: "nav-reports" },
  { to: "/admin/auditoria", icon: History, label: "Auditoria", testid: "nav-audit", adminOnly: true },
  { to: "/admin/usuarios", icon: UsersRound, label: "Usuários", testid: "nav-users", adminOnly: true },
  { to: "/admin/configuracoes", icon: Settings, label: "Configurações", testid: "nav-settings", adminOnly: true },
];

const STORAGE_KEY = "renascer:admin:sidebar-collapsed";

function getInitialCollapsed() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "1";
  } catch {}
  // Default: collapsed on mobile, expanded on desktop
  if (typeof window !== "undefined") {
    return window.innerWidth < 768;
  }
  return false;
}

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const visibleLinks = links.filter((l) => !l.adminOnly || isAdmin);

  const [collapsed, setCollapsed] = useState(getInitialCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  const toggle = () => setCollapsed((c) => !c);

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-warm)" }}>
      <aside
        className={`shrink-0 bg-white border-r border-stone-200 flex flex-col transition-[width] duration-200 ease-out ${
          collapsed ? "w-16" : "w-64"
        }`}
        data-testid="admin-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Header */}
        <div className={`border-b border-stone-200 ${collapsed ? "p-3" : "p-6"}`}>
          <Link to="/" className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <img src={LOGO_URL} alt="Renascer" className={collapsed ? "h-10 w-10 object-contain" : "h-11 w-11 object-contain"} />
            {!collapsed && (
              <div className="leading-tight">
                <div className="font-display text-lg font-bold">Renascer</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Admin</div>
              </div>
            )}
          </Link>
        </div>

        {/* Toggle button */}
        <div className={`px-3 pt-3 pb-1 flex ${collapsed ? "justify-center" : "justify-end"}`}>
          <button
            type="button"
            onClick={toggle}
            data-testid="admin-sidebar-toggle"
            aria-label={collapsed ? "Expandir barra lateral" : "Recolher barra lateral"}
            title={collapsed ? "Expandir" : "Recolher"}
            className="h-8 w-8 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 flex items-center justify-center text-stone-500 hover:text-stone-800 transition-colors"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className={`flex-1 space-y-1 ${collapsed ? "p-2" : "p-4"}`}>
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              title={collapsed ? l.label : undefined}
              className={({ isActive }) =>
                `group relative flex items-center rounded-xl text-sm font-medium transition-colors ${
                  collapsed ? "justify-center h-11 w-11 mx-auto" : "gap-3 px-3 py-2.5"
                } ${
                  isActive
                    ? "bg-gradient-to-r from-red-700 to-orange-600 text-white shadow-md"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              <l.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{l.label}</span>}
              {collapsed && (
                <span
                  className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-stone-900 text-white text-xs font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg"
                >
                  {l.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className={`border-t border-stone-200 ${collapsed ? "p-2 space-y-1" : "p-4 space-y-3"}`}>
          {collapsed ? (
            <>
              <Link
                to="/"
                title="Ver site público"
                data-testid="view-site-link"
                className="group relative flex items-center justify-center h-11 w-11 mx-auto rounded-xl text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-stone-900 text-white text-xs font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                  Ver site público
                </span>
              </Link>
              <button
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                title="Sair"
                aria-label="Sair"
                className="group relative flex items-center justify-center h-11 w-11 mx-auto rounded-xl text-red-700 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-stone-900 text-white text-xs font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                  Sair
                </span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/"
                className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-900 px-3 py-2"
                data-testid="view-site-link"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Ver site público
              </Link>
              <div className="px-3 pb-2">
                <div className="text-xs text-stone-500">Conectado como</div>
                <div className="text-sm font-medium text-stone-900 truncate" data-testid="admin-user-email">
                  {user?.email}
                </div>
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200" data-testid="admin-user-role">
                  {isAdmin ? "Administrador" : "Visualizador"}
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-700 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4" /> Sair
              </button>
            </>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
